import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../utils/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import PageHeader1 from '../../components/common/PageHeader1';

const AttendanceHistoryScreen = () => {
    const { driverId } = useParams();
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [driver, setDriver] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
    });

    useEffect(() => {
        fetchDriverDetails();
        fetchAttendanceHistory();
    }, [driverId]);

    const fetchDriverDetails = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/drivers/${driverId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDriver(response.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to fetch driver details');
        }
    };

    const fetchAttendanceHistory = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            let url = `accounts/attendance/driver/${driverId}/history`;
            
            // Add query parameters based on filter type
            const params = new URLSearchParams();
            if (filterType) {
                params.append('filter_type', filterType);
                if (filterType === 'custom') {
                    params.append('start_date', dateRange.startDate);
                    params.append('end_date', dateRange.endDate);
                }
            }
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await apiClient.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHistory(response.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to fetch attendance history');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (type) => {
        setFilterType(type);
        setDateRange({
            startDate: moment().format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD')
        });
    };

    useEffect(() => {
        fetchAttendanceHistory();
    }, [filterType]);

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 
                    pagetitle={`Attendance History - ${driver?.name || ''}`}
                    buttonText='Back'
                    onButtonClick={() => navigate('/attendance')}
                    permission="attendance.view"
                />
                
                <div className="row g-3 mb-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row align-items-center">
                                    <div className="col">
                                        <div className="btn-group" role="group">
                                            <button 
                                                className={`btn ${filterType === '' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleFilterChange('')}
                                            >
                                                All Time
                                            </button>
                                            <button 
                                                className={`btn ${filterType === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleFilterChange('monthly')}
                                            >
                                                This Month
                                            </button>
                                            <button 
                                                className={`btn ${filterType === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleFilterChange('weekly')}
                                            >
                                                This Week
                                            </button>
                                        </div>
                                    </div>
                                    {filterType === 'custom' && (
                                        <div className="col-auto">
                                            <div className="input-group">
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={dateRange.startDate}
                                                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                                />
                                                <span className="input-group-text">to</span>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={dateRange.endDate}
                                                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                                />
                                                <button 
                                                    className="btn btn-primary" 
                                                    onClick={fetchAttendanceHistory}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-auto">
                                        <button 
                                            className={`btn ${filterType === 'custom' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => handleFilterChange('custom')}
                                        >
                                            Custom Range
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center p-4">Loading...</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Check In Time</th>
                                                    <th>Check Out Time</th>
                                                    <th>Status</th>
                                                    <th>Duration</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {history.map((record) => (
                                                    <tr key={record.id}>
                                                        <td>{moment(record.date).format('DD/MM/YYYY')}</td>
                                                        <td>{record.check_in_time}</td>
                                                        <td>{record.check_out_time || '-'}</td>
                                                        <td>
                                                            <span className={`badge ${record.status === 'check-in' ? 'bg-success' : 'bg-warning'}`}>
                                                                {record.status === 'check-in' ? 'Checked In' : 'Checked Out'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {record.check_out_time ? 
                                                                (() => {
                                                                    const checkIn = moment(record.date + ' ' + record.check_in_time, 'YYYY-MM-DD HH:mm:ss');
                                                                    const checkOut = moment(record.date + ' ' + record.check_out_time, 'YYYY-MM-DD HH:mm:ss');
                                                                    const duration = moment.duration(checkOut.diff(checkIn));
                                                                    const hours = Math.floor(duration.asHours());
                                                                    const minutes = duration.minutes();
                                                                    return `${hours}h ${minutes}m`;
                                                                })()
                                                                : '-'
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                                {history.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">No attendance records found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceHistoryScreen;
